import type { Customer, Lang } from '../data/types';
import { useDashboard } from '../data/context';
import { t } from '../i18n/t';
import { Avatar } from '../components/Avatar';

interface InsightsViewProps {
  lang: Lang;
  onOpenCustomer: (c: Customer) => void;
}

interface Stat {
  k: string;
  v: string;
  suf?: string;
  d: string;
  up: boolean;
  ds: string;
}

export function InsightsView({ lang, onOpenCustomer }: InsightsViewProps) {
  const { insights, customers } = useDashboard();
  const maxCovers = Math.max(...insights.coversWeek.map((d) => d.v));
  const todayIdx = 3; // Ven (Friday) in the prototype's reference dataset

  const stats: Stat[] = [
    { k: t('coversToday', lang), v: '72', d: '+18%', up: true, ds: t('vsLastWeek', lang) },
    {
      k: t('occupancy', lang),
      v: '86',
      suf: '%',
      d: '37 / 42',
      up: true,
      ds: t('ofCapacity', lang),
    },
    { k: t('bookingsToday', lang), v: '13', d: '3', up: true, ds: t('pending', lang) },
    { k: t('noshowRate', lang), v: '4', suf: '%', d: '−1.2%', up: true, ds: t('vsLastWeek', lang) },
  ];

  return (
    <div className="view">
      <div className="view-pad">
        <div className="statgrid">
          {stats.map((s, i) => (
            <div className="statcard" key={i}>
              <div className="k">{s.k}</div>
              <div className="v tnum">
                {s.v}
                {s.suf && <span className="serif">{s.suf}</span>}
              </div>
              <div className={'d ' + (s.up ? 'up' : 'down')}>
                <span className="tnum">{s.d}</span>
                <span style={{ color: 'var(--mute)' }}>{s.ds}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="cards2">
          <div className="cardbox">
            <h3>
              {t('coversByDay', lang)}
              <span className="sub">{t('thisWeek', lang)}</span>
            </h3>
            <div className="bars">
              {insights.coversWeek.map((d, i) => (
                <div className="bar-col" key={i}>
                  <div className="bar" style={{ height: '100%' }}>
                    <div
                      className="fill"
                      style={{
                        height: `${(d.v / maxCovers) * 100}%`,
                        background: i === todayIdx ? 'var(--terra-deep)' : undefined,
                      }}
                    />
                  </div>
                  <div className="bar-x">{d.d[lang]}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="cardbox">
            <h3>{t('bookingSources', lang)}</h3>
            {insights.sources.map((s, i) => (
              <div className="hbar" key={i}>
                <div className="hbar-top">
                  <span className="hn">{s.name[lang]}</span>
                  <span className="hv tnum">{s.v}%</span>
                </div>
                <div className="hbar-track">
                  <div className="hbar-fill" style={{ width: `${s.v}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="cards2" style={{ gridTemplateColumns: '1fr' }}>
          <div className="cardbox">
            <h3>{t('topRegulars', lang)}</h3>
            {insights.regulars.map((rg) => {
              const c = customers.find((x) => x.id === rg.id);
              const isVip = c?.tags.includes('vip');
              return (
                <div
                  key={rg.id}
                  className="lrow"
                  style={{ cursor: 'pointer' }}
                  onClick={() => c && onOpenCustomer(c)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && c) {
                      e.preventDefault();
                      onOpenCustomer(c);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={rg.n}
                >
                  <Avatar name={rg.n} size="sm" />
                  <div className="lr-body">
                    <div className="lr-name">{rg.n}</div>
                    <div className="lr-sub">
                      {isVip ? `${t('tag_vip', lang)} · ` : ''}
                      {c?.fav}
                    </div>
                  </div>
                  <div className="lr-val">
                    {rg.v}{' '}
                    <span style={{ fontSize: 11, color: 'var(--mute)', fontWeight: 500 }}>
                      {t('visits', lang)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
